import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Dummy async thunk for API integration
export const addEmployeeApi = createAsyncThunk(
  'addEmployee/submitEmployee',
  async (employeeData, { rejectWithValue }) => {
    try {
      // Dummy API call - replace with actual API integration

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Dummy response
      const response = {
        success: true,
        employeeId: `EMP${Date.now()}`,
        message: 'Employee added successfully',
        data: employeeData
      };

      return response;
    } catch (error) {
      console.error('❌ API Error:', error);
      return rejectWithValue(error.message || 'Failed to add employee');
    }
  }
);

const addEmployeeSlice = createSlice({
  name: 'addEmployee',
  initialState: {
    // Consolidated employee data from all forms
    employeeData: {
      basicDetails: {
        firstName: "",
        middleName: "",
        lastName: "",
        personalEmail: "",
        personalPhone: "",
        alternativePhone: "",
        gender: "",
        dob: "",
        nationality: "Indian",
        religion: "",
        bloodGroup: "",
        maritalStatus: "",
        spouseName: "",
        physicallyChallenged: "No",
        emergencyRelation: "",
        emergencyName: "",
        emergencyPhone: "",
        refName: "",
        refPhone: "",
        refEmail: "",
        presentAddress: {
          accommodationType: "",
          houseNo: "",
          floor: "",
          buildingName: "",
          area: "",
          landmark: "",
          city: "",
          state: "",
          pincode: "",
        },
        permanentAddress: {
          houseNo: "",
          floor: "",
          buildingName: "",
          area: "",
          landmark: "",
          city: "",
          state: "",
          pincode: "",
        },
        sameAsPresent: true,
        aadharNo: "",
        aadharName: "",
        aadharFiles: [],
        panNo: "",
        panName: "",
        panFiles: [],
        passportNo: "",
        passportName: "",
        passportExpiry: "",
        passportFiles: [],
      },
      educationList: [
        {
          qualificationLevel: "",
          degree: "",
          yearOfJoining: "",
          yearOfCompletion: "",
          collegeName: "",
          university: "",
          cgpa: "",
          state: "",
          attachments: [],
        },
      ],
      employmentList: [
        {
          companyName: "",
          designation: "",
          ctc: "",
          joiningDate: "",
          relievingDate: "",
          address: "",
          hrName: "",
          hrPhone: "",
          hrEmail: "",
          managerName: "",
          managerPhone: "",
          references: [
            {
              name: "",
              phone: "",
              email: "",
            },
          ],
          offerLetterFiles: [],
          incrementLetterFiles: [],
          payslipFiles: [],
          experienceLetterFiles: [],
          relievingLetterFiles: [],
          isLatest: false,
        },
      ],
      profileDetails: {
        department: "",
        role: "",
        location: "",
        tempId: "",
        profilePicture: null,
      }
    },
    // API states
    loading: false,
    error: null,
    success: false,
    lastSubmittedData: null,
  },
  reducers: {
    // Update basic details
    setEmployeeBasicDetails: (state, action) => {
      if (action.payload && typeof action.payload === 'object') {
        Object.assign(state.employeeData.basicDetails, action.payload);
      }
    },

    // Update address fields
    updateEmployeeAddressField: (state, action) => {
      const { addressType, field, value } = action.payload;
      if (!addressType || !field) return;

      if (!state.employeeData.basicDetails[addressType]) {
        state.employeeData.basicDetails[addressType] = {};
      }

      state.employeeData.basicDetails[addressType][field] = value;
    },

    // Update education list
    setEmployeeEducationList: (state, action) => {
      state.employeeData.educationList = action.payload;
    },

    // Update employment list
    setEmployeeEmploymentList: (state, action) => {
      state.employeeData.employmentList = action.payload;
    },

    // Update profile details
    setEmployeeProfileDetails: (state, action) => {
      if (action.payload && typeof action.payload === 'object') {
        Object.assign(state.employeeData.profileDetails, action.payload);
      }
    },

    // Update specific education item
    updateEmployeeEducationItem: (state, action) => {
      const { index, field, value } = action.payload;
      if (typeof index !== 'number' || index < 0 || !field) return;

      const item = state.employeeData.educationList[index];
      if (item) {
        item[field] = value;
      }
    },

    // Update specific employment item
    updateEmployeeEmploymentItem: (state, action) => {
      const { index, field, value } = action.payload;
      if (typeof index !== 'number' || index < 0 || !field) return;

      const item = state.employeeData.employmentList[index];
      if (item) {
        item[field] = value;
      }
    },

    // Reset all employee data
    resetEmployeeData: (state) => {
      state.employeeData = {
        basicDetails: {
          firstName: "",
          middleName: "",
          lastName: "",
          personalEmail: "",
          personalPhone: "",
          alternativePhone: "",
          gender: "",
          dob: "",
          nationality: "Indian",
          religion: "",
          bloodGroup: "",
          maritalStatus: "",
          spouseName: "",
          physicallyChallenged: "No",
          emergencyRelation: "",
          emergencyName: "",
          emergencyPhone: "",
          refName: "",
          refPhone: "",
          refEmail: "",
          presentAddress: {
            accommodationType: "",
            houseNo: "",
            floor: "",
            buildingName: "",
            area: "",
            landmark: "",
            city: "",
            state: "",
            pincode: "",
          },
          permanentAddress: {
            houseNo: "",
            floor: "",
            buildingName: "",
            area: "",
            landmark: "",
            city: "",
            state: "",
            pincode: "",
          },
          sameAsPresent: true,
          aadharNo: "",
          aadharName: "",
          aadharFiles: [],
          panNo: "",
          panName: "",
          panFiles: [],
          passportNo: "",
          passportName: "",
          passportExpiry: "",
          passportFiles: [],
        },
        educationList: [
          {
            qualificationLevel: "",
            degree: "",
            yearOfJoining: "",
            yearOfCompletion: "",
            collegeName: "",
            university: "",
            cgpa: "",
            state: "",
            attachments: [],
          },
        ],
        employmentList: [
          {
            companyName: "",
            designation: "",
            ctc: "",
            joiningDate: "",
            relievingDate: "",
            address: "",
            hrName: "",
            hrPhone: "",
            hrEmail: "",
            managerName: "",
            managerPhone: "",
            references: [
              {
                name: "",
                phone: "",
                email: "",
              },
            ],
            offerLetterFiles: [],
            incrementLetterFiles: [],
            payslipFiles: [],
            experienceLetterFiles: [],
            relievingLetterFiles: [],
            isLatest: false,
          },
        ],
        profileDetails: {
          department: "",
          role: "",
          location: "",
          tempId: "",
          profilePicture: null,
        }
      };
      state.loading = false;
      state.error = null;
      state.success = false;
      state.lastSubmittedData = null;
    },

    // Clear error state
    clearEmployeeError: (state) => {
      state.error = null;
    },

    // Clear success state
    clearEmployeeSuccess: (state) => {
      state.success = false;
      state.lastSubmittedData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addEmployeeApi.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addEmployeeApi.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.lastSubmittedData = action.payload;
      })
      .addCase(addEmployeeApi.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
        console.error('💥 Failed to add employee:', action.payload);
      });
  },
});

export const {
  setEmployeeBasicDetails,
  updateEmployeeAddressField,
  setEmployeeEducationList,
  setEmployeeEmploymentList,
  setEmployeeProfileDetails,
  updateEmployeeEducationItem,
  updateEmployeeEmploymentItem,
  resetEmployeeData,
  clearEmployeeError,
  clearEmployeeSuccess,
} = addEmployeeSlice.actions;

export default addEmployeeSlice.reducer;
